// =============================================================================
// app/settings/index.tsx — Settings hub (bare-RN port of apps/cryb-native).
//
// MIGRATION (P3 settings):
//   - expo-router (Stack/useRouter/router.push) -> react-navigation
//     (useNavigation + navigation.navigate against SettingsStack/RootStack names).
//   - @expo/vector-icons Ionicons -> components/ui/Icon.
//   - expo-constants version -> services/app-config getVersionDisplay()
//     (real 1.3.1 (61) via react-native-device-info; NO expo-constants).
//   - The inline <Stack.Screen options> header is dropped — the SettingsStack
//     navigator owns the header/title.
// =============================================================================
import { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  useWindowDimensions,
  Linking,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '../../components/ui/Icon';
import { logout } from '../../services/api';
import { getVersionDisplay } from '../../services/app-config';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';
import { GlassSurface, glassTokens } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';
import ScreenTip from '../../components/ui/ScreenTip';
import { SHOW_CRYPTO, SHOW_DEVELOPER, CRYPTO_LIVE } from '../../lib/comingSoon';

// Real shipped version/build — reads react-native-device-info so it never goes
// stale (shows 1.3.1 (61)). Replaces the old expo-constants read.
const VERSION_DISPLAY = getVersionDisplay();

// Terms/Privacy open the IN-APP native LegalScreen (Apple 1.2 / 5.1.1 — no
// external browser). Help Center remains an external link.
const LEGAL_DOCS: Record<string, 'terms' | 'privacy' | 'guidelines'> = {
  'Terms of Service': 'terms',
  'Privacy Policy': 'privacy',
  'Community Guidelines': 'guidelines',
};
const LEGAL_URLS: Record<string, string> = {
  'Help Center': 'https://platform.cryb.ai/help',
};

interface SettingsItem {
  icon: string;
  label: string;
  // react-navigation route name (resolved against SettingsStack or RootStack).
  route?: string;
  color?: string;
  // Crypto is v2: the row stays VISIBLE so users see what's coming, but the tap
  // is disabled and a "Coming soon" pill replaces the chevron. When SHOW_CRYPTO
  // flips on, these rows get their real routes back (see the sections below).
  comingSoon?: boolean;
}

// Crypto/wallet rows: visible always, but only tappable once the feature is
// actually LIVE (CRYPTO_LIVE, v2). Gated on CRYPTO_LIVE (not SHOW_CRYPTO) so
// these Settings rows show a Coming-soon pill while the crypto backend is off —
// they looked fully functional (chevron + route) even though nothing works yet.
const cryptoRow = (item: SettingsItem): SettingsItem =>
  CRYPTO_LIVE ? item : { ...item, route: undefined, comingSoon: true };

// Developer rows: same v1-gated treatment as cryptoRow. Visible always, live
// only when SHOW_DEVELOPER (v2). While gated they carry no route (nothing to
// dead-end into) + a Coming-soon pill.
const devRow = (item: SettingsItem): SettingsItem =>
  SHOW_DEVELOPER ? item : { ...item, route: undefined, comingSoon: true };

const SETTINGS_SECTIONS: { title?: string; items: SettingsItem[] }[] = [
  {
    title: 'Account',
    items: [
      { icon: 'person-outline', label: 'Account', route: 'SettingsAccount' },
      { icon: 'shield-checkmark-outline', label: 'Security', route: 'SettingsSecurity' },
      { icon: 'lock-closed-outline', label: 'Privacy', route: 'SettingsPrivacy' },
      // 'Wallet' is not registered in any navigator, so it must never route —
      // always gate it behind the Coming-soon pill (matches lib/comingSoon policy).
      { icon: 'wallet-outline', label: 'Wallet & Payments', comingSoon: true },
    ],
  },
  {
    title: 'Crypto & Trading',
    items: [
      cryptoRow({ icon: 'swap-horizontal-outline', label: 'Exchange Accounts', color: '#F7931A', route: 'SettingsExchanges' }),
      cryptoRow({ icon: 'analytics-outline', label: 'Trading Preferences', route: 'CopySettings' }),
      cryptoRow({ icon: 'pulse-outline', label: 'Price Alerts', route: 'PriceAlerts' }),
      cryptoRow({ icon: 'star-outline', label: 'Watchlist', route: 'Watchlist' }),
      cryptoRow({ icon: 'cash-outline', label: 'Crypto Tips', route: 'Tips' }),
      cryptoRow({ icon: 'bar-chart-outline', label: 'Tax Report', route: 'TaxReport' }),
    ],
  },
  // Linked Accounts (social logins) stay hidden until the OAuth connect flow is
  // real — they return via SHOW_CRYPTO alongside the full v2 reveal.
  ...(SHOW_CRYPTO ? [{
    title: 'Linked Accounts',
    items: [
      { icon: 'logo-twitter', label: 'Twitter / X', color: '#1DA1F2' },
      { icon: 'logo-discord', label: 'Discord', color: '#5865F2' },
    ],
  }] : []),
  {
    title: 'Preferences',
    items: [
      { icon: 'notifications-outline', label: 'Notifications', route: 'SettingsNotifications' },
      { icon: 'color-palette-outline', label: 'Appearance', route: 'SettingsAppearance' },
      { icon: 'language-outline', label: 'Language', route: 'SettingsLanguage' },
    ],
  },
  {
    title: 'People',
    items: [
      { icon: 'ban-outline', label: 'Blocked Users', route: 'SettingsBlocked' },
      { icon: 'volume-mute-outline', label: 'Muted Users', route: 'SettingsMuted' },
      { icon: 'text-outline', label: 'Muted Words', route: 'SettingsMutedWords' },
    ],
  },
  {
    title: 'Data & Privacy',
    items: [
      { icon: 'document-lock-outline', label: 'Data Privacy', route: 'SettingsDataPrivacy' },
    ],
  },
  // Developer platform is v2: rows stay VISIBLE (FOMO into the CRYB v2 developer
  // surface) but are non-functional in v1 — no route, a Coming-soon pill. When
  // SHOW_DEVELOPER flips on, devRow() restores their real routes.
  {
    title: 'Developer',
    items: [
      devRow({ icon: 'key-outline', label: 'API Keys', route: 'SettingsDeveloper' }),
      devRow({ icon: 'git-network-outline', label: 'Webhooks', route: 'SettingsDeveloper' }),
      devRow({ icon: 'terminal-outline', label: 'CLI & MCP Server', route: 'SettingsDeveloper' }),
      devRow({ icon: 'code-slash-outline', label: 'API Reference', route: 'SettingsDeveloper' }),
    ],
  },
  // Resources removed: 'LearnIndex' and 'Communities' aren't registered in any
  // navigator (the real screens live under different/tab names), so these rows
  // were dead links. Re-add here only once those root routes are registered.
  {
    title: 'Support',
    items: [
      { icon: 'help-circle-outline', label: 'Help Center' },
      { icon: 'document-text-outline', label: 'Terms of Service' },
      { icon: 'shield-outline', label: 'Privacy Policy' },
    ],
  },
];

// Linked-account rows whose connect flow isn't live yet — shown with a
// "Coming soon" badge and a disabled (informational) tap.
const SOCIAL_LABELS = ['Twitter / X', 'Discord'];

export default function SettingsScreen() {
  const navigation = useNavigation<any>();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  // On tablet/desktop the content is width-capped + centered; the section list
  // splits into two balanced columns so wide viewports don't leave a lonely
  // single column of rows with acres of empty space on either side.
  const twoColumn = layout !== 'phone';
  const containerStyle = twoColumn
    ? { maxWidth: layout === 'desktop' ? 900 : 720, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;
  const [loggingOut, setLoggingOut] = useState(false);

  function handleLogout() {
    Alert.alert('Log Out', 'Are you sure you want to log out of CRYB?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Log Out',
        style: 'destructive',
        onPress: async () => {
          setLoggingOut(true);
          try {
            // logout() clears tokens + flips the auth state; the root useAuth()
            // gate swaps to the AuthStack — no manual navigation needed.
            await logout();
          } finally {
            setLoggingOut(false);
          }
        },
      },
    ]);
  }

  function handleItemPress(item: SettingsItem) {
    // Coming-soon rows are disabled at the TouchableOpacity level; this is a
    // belt-and-braces guard so a gated row can never navigate anywhere.
    if (item.comingSoon) return;
    if (item.route) {
      // react-navigation resolves the name against the nested SettingsStack or
      // bubbles up to the RootStack (Wallet/LearnIndex/Communities/crypto).
      navigation.navigate(item.route as never);
      return;
    }
    // Legal docs open in-app (native LegalScreen).
    if (LEGAL_DOCS[item.label]) {
      navigation.navigate('Legal' as never, { doc: LEGAL_DOCS[item.label] } as never);
      return;
    }
    // Support links (Help Center) — external.
    if (LEGAL_URLS[item.label]) {
      Linking.openURL(LEGAL_URLS[item.label]).catch(() => {
        Alert.alert('Could not open link', 'Please visit cryb.ai for more information.');
      });
      return;
    }
    // Linked social accounts — connect flow isn't live yet (no OAuth provider
    // credentials configured), so surface "coming soon" instead of a dead link.
    if (SOCIAL_LABELS.includes(item.label)) {
      Alert.alert('Coming soon', `${item.label} linking will be available soon.`);
      return;
    }
  }

  const renderSection = (section: (typeof SETTINGS_SECTIONS)[number], key: React.Key) => (
    // In two-column mode the parent columnsRow owns the page gutter, so the
    // section drops its own horizontal padding to avoid a double gutter.
    <View key={key} style={[styles.section, twoColumn && styles.sectionInColumn]}>
      {section.title && <Text style={styles.sectionTitle}>{section.title}</Text>}
      <GlassSurface variant="card" radius={glassTokens.radius.lg} contentStyle={styles.sectionCardContent}>
        {section.items.map((item, itemIdx) => (
          <TouchableOpacity
            key={itemIdx}
            style={[
              styles.settingsRow,
              itemIdx < section.items.length - 1 && styles.settingsRowBorder,
            ]}
            onPress={() => handleItemPress(item)}
            disabled={!!item.comingSoon}
            activeOpacity={0.6}
            accessibilityRole="button"
            accessibilityLabel={item.comingSoon ? `${item.label}, coming soon` : item.label}
            accessibilityState={{ disabled: !!item.comingSoon }}
          >
            <View style={[styles.settingsLeft, item.comingSoon && styles.rowDimmed]}>
              <View
                style={[
                  styles.iconWrap,
                  // Faint per-row tint: colored rows (crypto/social) get their
                  // brand hue for subtle section identity; the rest use primary.
                  { backgroundColor: item.color ? item.color + '1A' : colors.primary + '12' },
                ]}
              >
                <Ionicons
                  name={item.icon as any}
                  size={20}
                  color={item.color || colors.primary}
                />
              </View>
              <Text style={styles.settingsLabel} numberOfLines={1}>{item.label}</Text>
            </View>
            {item.comingSoon || SOCIAL_LABELS.includes(item.label) ? (
              <View style={styles.comingSoonBadge}>
                <Text style={styles.comingSoonText}>Coming soon</Text>
              </View>
            ) : (
              <Ionicons name="chevron-forward" size={18} color={colors.faint} />
            )}
          </TouchableOpacity>
        ))}
      </GlassSurface>
    </View>
  );

  // Two-column split for tablet/desktop: distribute sections across the two
  // columns keeping the taller (more items) blocks balanced left→right.
  const columns: (typeof SETTINGS_SECTIONS)[] = [[], []];
  if (twoColumn) {
    const heights = [0, 0];
    SETTINGS_SECTIONS.forEach((section) => {
      const col = heights[0] <= heights[1] ? 0 : 1;
      columns[col].push(section);
      heights[col] += section.items.length + 1; // +1 for the section title row
    });
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <ScrollView
        contentContainerStyle={[styles.scrollContent, containerStyle]}
        showsVerticalScrollIndicator={false}
      >
        <ScreenTip
          tipKey="settings"
          title="Settings"
          text="Manage your account, privacy and preferences."
        />

        {twoColumn ? (
          <View style={styles.columnsRow}>
            {columns.map((col, colIdx) => (
              <View key={colIdx} style={styles.column}>
                {col.map((section, i) => renderSection(section, `${colIdx}-${i}`))}
              </View>
            ))}
          </View>
        ) : (
          SETTINGS_SECTIONS.map((section, sectionIdx) => renderSection(section, sectionIdx))
        )}

        <Text style={styles.versionText}>CRYB v{VERSION_DISPLAY}</Text>

        <TouchableOpacity
          style={[styles.logoutBtn, twoColumn && styles.logoutBtnWide]}
          onPress={handleLogout}
          disabled={loggingOut}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Log out of CRYB"
        >
          {loggingOut ? (
            <ActivityIndicator size="small" color={colors.error} />
          ) : (
            <>
              <Ionicons name="log-out-outline" size={20} color={colors.error} />
              <Text style={styles.logoutText}>Log Out</Text>
            </>
          )}
        </TouchableOpacity>

        <View style={{ height: spacing.xxxl }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    // Transparent so the full-bleed ParticleBackground shows through; the
    // section GlassSurface cards provide the readable translucent surfaces.
    backgroundColor: 'transparent',
  },
  scrollContent: {
    // Extra top padding so the first section clears the translucent floating
    // header and content visibly scrolls UNDER the material (skill §12).
    paddingTop: spacing.xl,
    paddingBottom: spacing.xxxl,
  },
  // Two-column layout (tablet/desktop). The row lays out the two balanced
  // columns side-by-side with a consistent gutter between them.
  columnsRow: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    gap: spacing.lg,
  },
  column: {
    flex: 1,
  },
  section: {
    marginBottom: spacing.lg,
    paddingHorizontal: spacing.lg,
  },
  sectionInColumn: {
    paddingHorizontal: 0,
  },
  sectionTitle: {
    ...typographyScale.caption,
    fontSize: 13,
    color: colors.muted,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    ...fonts.semibold,
  },
  sectionCardContent: {
    padding: 0,
    overflow: 'hidden',
    borderRadius: glassTokens.radius.lg,
  },
  settingsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  settingsRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  settingsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: radius.md,
    // backgroundColor set inline per-row for section identity (see render).
    justifyContent: 'center',
    alignItems: 'center',
  },
  settingsLabel: {
    fontSize: 16,
    color: colors.ink,
    ...fonts.medium,
  },
  rowDimmed: {
    opacity: 0.55,
  },
  comingSoonBadge: {
    backgroundColor: colors.primary + '1F',
    borderRadius: radius.full,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.primary + '33',
  },
  comingSoonText: {
    fontSize: 11,
    color: colors.primary,
    ...fonts.semibold,
  },
  versionText: {
    ...typographyScale.caption,
    textAlign: 'center',
    fontSize: 13,
    color: colors.faint,
    marginTop: spacing.lg,
    marginBottom: spacing.lg,
    ...fonts.regular,
  },
  logoutBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.error + '10',
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.error + '20',
  },
  // On wide viewports keep the log-out button a compact centered pill rather
  // than a full-bleed bar spanning the whole capped column.
  logoutBtnWide: {
    alignSelf: 'center',
    minWidth: 280,
    paddingHorizontal: spacing.xxxl,
  },
  logoutText: {
    fontSize: 16,
    color: colors.error,
    ...fonts.semibold,
  },
});
